import { ref, onUnmounted, watch, nextTick, type Ref } from 'vue';
import type { Swiper } from 'swiper';
import { hasNavigation, hasPagination, hasScrollbar } from './helpers/swiperHelpers';

const makeArray = <T>(value: T | T[] | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

/**
 * Detects user interactions with Swiper and tracks carousel scroll events.
 * Sets a flag on user interaction (touch, click, keyboard), then tracks on
 * `realIndexChange` when the flag is set. DOM click listeners use capture phase
 * to ensure the flag is set before Swiper's synchronous slide operations.
 */
export function useSwiperInteractionDetector(
  swiperInstanceRef: Ref<Swiper | null>,
  onScrollTrackedCallback: (direction: 'next' | 'prev') => void
): void {
  const initialized = ref(false);
  const isUserInteraction = ref(false);
  let swiper: Swiper | null = null;
  const realPrevIndex = ref(-1);

  const handleUserInteraction = () => {
    isUserInteraction.value = true;
  };

  const onRealSlideChange = () => {
    const currentRealIndex = swiper?.realIndex;
    const previousRealIndex = realPrevIndex.value;

    updateRealPrevIndex();

    if (!isUserInteraction.value) return;
    isUserInteraction.value = false;

    if (currentRealIndex === undefined || currentRealIndex === previousRealIndex) {
      return;
    }

    const direction: 'next' | 'prev' = currentRealIndex > previousRealIndex ? 'next' : 'prev';

    if (onScrollTrackedCallback) {
      onScrollTrackedCallback(direction);
    }
  };

  const updateRealPrevIndex = () => {
    if (swiper) {
      realPrevIndex.value = swiper.realIndex;
    }
  };

  watch(
    swiperInstanceRef,
    (newSwiperInstance) => {
      if (newSwiperInstance && !initialized.value) {
        swiper = newSwiperInstance;
        updateRealPrevIndex();
        swiper.on('touchStart', handleUserInteraction);
        swiper.on('realIndexChange', onRealSlideChange);
        // Defer nav/pagination/scrollbar listener attachment to nextTick.
        // Swiper initializes synchronously, but navigation elements (gated by
        // component lifecycle) may not be in DOM yet. Deferring listener attachment ensures
        // nav elements are available before attachment.
        nextTick(() => {
          if (hasNavigation(swiper)) {
            const nextEls = makeArray(swiper.navigation.nextEl);
            const prevEls = makeArray(swiper.navigation.prevEl);
            nextEls.forEach((el) => el?.addEventListener?.('click', handleUserInteraction, true));
            prevEls.forEach((el) => el?.addEventListener?.('click', handleUserInteraction, true));
          }
          if (hasPagination(swiper)) {
            swiper.pagination.bullets.forEach((bullet) => {
              bullet.addEventListener('click', handleUserInteraction, true);
            });
          }
          if (hasScrollbar(swiper)) {
            swiper.scrollbar.el?.addEventListener?.('click', handleUserInteraction, true);
          }
        });
        initialized.value = true;
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    if (swiper && initialized.value) {
      swiper.off('touchStart', handleUserInteraction);
      swiper.off('realIndexChange', onRealSlideChange);
      if (hasNavigation(swiper)) {
        makeArray(swiper.navigation.nextEl).forEach(
          (el) => el?.removeEventListener?.('click', handleUserInteraction, true)
        );
        makeArray(swiper.navigation.prevEl).forEach(
          (el) => el?.removeEventListener?.('click', handleUserInteraction, true)
        );
      }
      if (hasPagination(swiper)) {
        swiper.pagination.bullets.forEach((bullet) => {
          bullet.removeEventListener('click', handleUserInteraction, true);
        });
      }
      if (hasScrollbar(swiper)) {
        swiper.scrollbar.el?.removeEventListener?.('click', handleUserInteraction, true);
      }
    }
  });
}
